/**
 * Configura o botão "Localizar Variáveis" para o editor TinyMCE
 * @param {Object} editor - Instância do editor TinyMCE
 */
export const setupFindVariablesButton = editor => {
  editor.ui.registry.addButton('findvariables', {
    text: 'Ir para próxima variável',
    tooltip: 'Destaca a próxima variável não preenchida no documento',
    onAction: () => {
      const firstVariable = editor.dom.select('.contract-variable')[0];
      if (firstVariable) {
        editor.selection.select(firstVariable);
        editor.selection.scrollIntoView(firstVariable);
      } else {
        alert('Não há variáveis para localizar no documento.');
      }
    },
  });
};

/**
 * Configura o tratamento de cliques em variáveis no editor
 * @param {Object} editor - Instância do editor TinyMCE
 */
export const setupVariableClickHandler = editor => {
  editor.on('click', e => {
    const clickedElement = e.target;
    let variableElement = null;

    if (
      clickedElement.classList &&
      clickedElement.classList.contains('contract-variable')
    ) {
      variableElement = clickedElement;
    } else if (
      clickedElement.parentNode &&
      clickedElement.parentNode.classList &&
      clickedElement.parentNode.classList.contains('contract-variable')
    ) {
      variableElement = clickedElement.parentNode;
    }

    if (variableElement) {
      editor.selection.select(variableElement);
      editor.focus();
      e.preventDefault();
      e.stopPropagation();
    }
  });
};

/**
 * Configura o tratamento de teclas para edição e navegação entre variáveis
 * @param {Object} editor - Instância do editor TinyMCE
 */
export const setupKeyboardHandlers = editor => {
  editor.on('keydown', e => {
    const selection = editor.selection;
    const selectedNode = selection.getNode();

    if (
      selectedNode &&
      selectedNode.classList &&
      selectedNode.classList.contains('contract-variable')
    ) {
      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          editor.dom.remove(selectedNode);
          e.preventDefault();
        } else {
          const newNode = editor.dom.create('span', {}, e.key);
          editor.dom.replace(newNode, selectedNode);
          selection.setCursorLocation(newNode, 1);
          e.preventDefault();
        }
      } else if (e.key === 'Tab') {
        e.preventDefault();

        const variables = editor.dom.select('.contract-variable');
        if (variables.length <= 1) return;

        let currentIndex = -1;
        for (let i = 0; i < variables.length; i++) {
          if (variables[i] === selectedNode) {
            currentIndex = i;
            break;
          }
        }

        let nextIndex;
        if (e.shiftKey) {
          nextIndex = currentIndex <= 0 ? variables.length - 1 : currentIndex - 1;
        } else {
          nextIndex = (currentIndex + 1) % variables.length;
        }

        const nextVariable = variables[nextIndex];
        if (nextVariable) {
          editor.selection.select(nextVariable);
          editor.selection.scrollIntoView(nextVariable);
          editor.focus();
        }
      }
    } else if (e.key === 'Tab') {
      const variables = editor.dom.select('.contract-variable');
      if (variables.length > 0) {
        const index = e.shiftKey ? variables.length - 1 : 0;
        const variable = variables[index];

        e.preventDefault();
        editor.selection.select(variable);
        editor.selection.scrollIntoView(variable);
        editor.focus();
      }
    }
  });
};

/**
 * Configura o editor TinyMCE com todas as funcionalidades personalizadas
 * @param {Object} editor - Instância do editor TinyMCE
 */
export const setupEditor = editor => {
  setupFindVariablesButton(editor);

  editor.on('init', () => {
    setupVariableClickHandler(editor);
    setupKeyboardHandlers(editor);
  });
};

/**
 * Retorna as configurações do editor TinyMCE
 * @param {Object} params - Parâmetros de configuração
 * @returns {Object} Configurações do editor
 */
export const getEditorConfig = ({
  isMobile,
  editorRef,
  editorScriptPath,
  translationPath,
}) => {
  return {
    tinymceScriptSrc: editorScriptPath,
    onInit: (_evt, editor) => (editorRef.current = editor),
    initialValue: '',
    init: {
      height: isMobile ? 400 : 650,
      menubar: false,
      toolbar_mode: 'wrap',
      selector: 'textarea',
      plugins: 'lists advlist table pagebreak searchreplace fullscreen',
      toolbar: isMobile
        ? 'bold italic | alignleft aligncenter alignright | numlist bullist | fullscreen print findvariables'
        : 'blocks fontsizeinput bold italic underline alignleft aligncenter alignright alignjustify lineheight numlist bullist | searchreplace pagebreak | fullscreen print findvariables',
      content_style:
        'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } ' +
        `.contract-variable { 
            display: inline-block; 
            background-color: #fffacd; 
            border: 1px solid #ffd700; 
            border-radius: 3px; 
            padding: 0 3px; 
            font-weight: bold; 
            color: #8b4513; 
            cursor: pointer; 
          }
            
          @media print {
            .contract-variable {
              background-color: transparent;
              border: none;
              color: #000;
              cursor: default;
            }
          }
          `,
      language_url: translationPath,
      language: 'pt_BR',
      browser_spellcheck: false,
      paste_data_images: false,
      setup: setupEditor,
      mobile: {
        theme: 'mobile',
        plugins: ['autosave', 'lists', 'autolink'],
        toolbar: ['undo', 'bold', 'italic', 'bullist', 'numlist', 'findvariables'],
      },
    },
  };
};
